import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import { getRandomTasks, getAgeAppropriateTasks } from '../../data/taskTemplates'
import toast from 'react-hot-toast'

const { FiPlus, FiRefreshCw, FiStar, FiClock, FiZap } = FiIcons

const QuickTaskSuggestions = ({ maxSuggestions = 6 }) => {
  const { createTask } = useFamily()
  const { family } = useAuth()
  const [loading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState(() => {
    // Get age-appropriate suggestions based on family children
    if (family?.children && family.children.length > 0) {
      const averageAge = family.children.reduce((sum, child) => sum + (child.age || 8), 0) / family.children.length
      return getAgeAppropriateTasks(averageAge).slice(0, maxSuggestions)
    }
    return getRandomTasks('daily', maxSuggestions)
  })

  const refreshSuggestions = () => {
    const categories = ['daily', 'weekly', 'kitchen', 'laundry', 'pets', 'organization']
    const randomCategory = categories[Math.floor(Math.random() * categories.length)]
    const newSuggestions = getRandomTasks(randomCategory, maxSuggestions)
    setSuggestions(newSuggestions)
    toast.success('Fresh task suggestions loaded! 🔄')
  }

  const handleAddTask = async (task) => {
    setLoading(true)
    
    try {
      const taskData = {
        title: task.title,
        description: task.description,
        points: task.points,
        category: task.category,
        estimated_time_minutes: task.estimatedTime,
        recurring_pattern: 'daily',
        start_date: new Date().toISOString().split('T')[0],
        family_id: family.id
      }

      const { error } = await createTask(taskData)
      
      if (error) {
        console.error('Error creating task:', error)
        toast.error('Failed to add task')
      } else {
        toast.success(`${task.title} added successfully! 🎉`)
        // Remove the added task from suggestions
        setSuggestions(prev => prev.filter(t => t.title !== task.title))
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const formatTimeDisplay = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  const getDifficultyColor = (points) => {
    if (points >= 30) return 'text-red-500'
    if (points >= 20) return 'text-orange-500'
    if (points >= 10) return 'text-yellow-500'
    return 'text-green-500'
  }

  if (suggestions.length === 0) {
    return (
      <Card className="p-6 text-center">
        <div className="text-4xl mb-3">✨</div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          All suggestions added!
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Get fresh task ideas for your family
        </p>
        <Button variant="primary" onClick={refreshSuggestions}>
          <SafeIcon icon={FiRefreshCw} />
          Get New Suggestions
        </Button>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Quick Task Suggestions
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Popular tasks perfect for your family
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshSuggestions}
          className="flex items-center gap-2"
        >
          <SafeIcon icon={FiRefreshCw} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suggestions.map((task, index) => (
          <motion.div
            key={task.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <Card className="p-4 hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-3 mb-3">
                <div className="text-2xl">{task.emoji}</div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {task.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {task.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Badge variant="primary" size="sm" className="flex items-center gap-1">
                  <SafeIcon icon={FiStar} className={`text-xs ${getDifficultyColor(task.points)}`} />
                  {task.points} pts
                </Badge>
                <Badge variant="secondary" size="sm" className="flex items-center gap-1">
                  <SafeIcon icon={FiClock} className="text-xs" />
                  {formatTimeDisplay(task.estimatedTime)}
                </Badge>
              </div>

              <Button
                variant="primary"
                size="sm"
                className="w-full"
                onClick={() => handleAddTask(task)}
                disabled={loading}
              >
                <SafeIcon icon={FiPlus} className="text-sm" />
                Add Task
              </Button>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <SafeIcon icon={FiZap} className="text-primary-600 dark:text-primary-400" />
          <span className="font-medium text-primary-800 dark:text-primary-300">Pro Tip</span>
        </div>
        <p className="text-sm text-primary-700 dark:text-primary-400">
          These suggestions are tailored based on your family's children ages and popular task categories. 
          Click "Refresh" to see different options, or use the full Task Library for more choices!
        </p>
      </div>
    </Card>
  )
}

export default QuickTaskSuggestions